import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { SlidersHorizontal } from 'lucide-react';
import { catalogueKeys, fetchProducts } from '@/api/catalogue';
import { ProductGrid } from '@/components/product/ProductGrid';
import { FilterPanel } from '@/components/catalogue/FilterPanel';
import { Button } from '@/components/ui/Button';
import { useProductFilters } from '@/hooks/useProductFilters';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import type { ProductSort } from '@/types/catalogue';
import { PageHeader } from '@/components/layout/PageHeader';

const SORT_OPTIONS: Array<{ value: ProductSort; label: string }> = [
  { value: 'newest', label: 'Newest first' },
  { value: 'featured', label: 'Featured' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
  { value: 'name_asc', label: 'Name: A–Z' },
];

/**
 * The shop — BUILD-PLAN §9, §27, §28, §41.
 *
 * Filters live in the URL (useProductFilters), so this page is shareable and
 * back-button correct. Layout is mobile-first: filters are a drawer on small
 * screens and a sidebar from lg upward.
 */
export function ShopPage() {
  const { filters, setFilter, toggleTerm, clearFilters, hasFilters } = useProductFilters();
  const [filtersOpen, setFiltersOpen] = useState(false);

  /*
   | How many filters are on, for the badge on the toggle.
   |
   | With the panel collapsed there is otherwise no way to tell that anything
   | is filtering the results — a customer sees a short list and assumes the
   | shop is small rather than that they narrowed it.
   */
  const activeFilterCount =
    (filters.categories?.length ?? 0) +
    (filters.occasions?.length ?? 0) +
    (filters.min_price !== undefined || filters.max_price !== undefined ? 1 : 0) +
    (filters.personalisable === true ? 1 : 0) +
    (filters.in_stock === true ? 1 : 0);

  usePageMetadata({
    title: 'Shop the Collection',
    description:
      'Luxury gift boxes, personalised gifts and bespoke packaging, thoughtfully curated by Glanz Luxe.',
  });

  const { data, isPending, isError, refetch, isPlaceholderData } = useQuery({
    queryKey: catalogueKeys.products(filters),
    queryFn: () => fetchProducts(filters),
    /*
     | Keeps the previous page visible while the next loads, so changing a
     | filter dims the grid rather than collapsing it to a skeleton and back.
     | Without this the page height jumps on every filter change.
     */
    placeholderData: keepPreviousData,
  });

  const products = data?.items ?? [];
  const meta = data?.meta;

  return (
    <div className="mx-auto max-w-7xl px-5 py-10 sm:px-6 lg:px-8 lg:py-14">
      <PageHeader
        eyebrow="The Collection"
        title="Shop"
        description="Every piece, filtered however you like."
        className="mb-8"
      />

      <div className="flex gap-10">
        {/* Desktop sidebar */}
        <aside className="hidden w-72 shrink-0 lg:block">
          <FilterPanel
            filters={filters}
            onToggleTerm={toggleTerm}
            onSetFilter={setFilter}
            onClear={clearFilters}
            hasFilters={hasFilters}
          />
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-6 flex items-center justify-between gap-4">
            <p className="text-sm text-muted" aria-live="polite">
              {isPending
                ? 'Loading…'
                : `${meta?.total ?? 0} ${meta?.total === 1 ? 'piece' : 'pieces'}`}
            </p>

            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-controls="shop-filters"
                className="lg:hidden"
              >
                <SlidersHorizontal className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                {filtersOpen ? 'Hide filters' : 'Filters'}
                {activeFilterCount > 0 && (
                  <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[0.625rem] text-white">
                    {activeFilterCount}
                  </span>
                )}
              </Button>

              <label className="flex items-center gap-2 text-sm">
                <span className="sr-only">Sort products by</span>
                <select
                  value={filters.sort ?? 'newest'}
                  onChange={(event) =>
                    setFilter({ sort: event.target.value as ProductSort })
                  }
                  className="min-h-9 rounded-sm border border-line bg-surface px-2.5 text-sm"
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {/*
            Filters open IN THE PAGE below `lg`, not in a side modal.
            ------------------------------------------------------------------
            A drawer puts a second scrolling context on top of a page that is
            already scrolling, so choosing a filter meant scrolling inside a
            narrow panel while the results sat hidden behind it — you could
            never see the effect of a choice while making it. In flow, the
            grid is directly underneath: pick a facet, the count above updates
            and the products are right there.

            It also removes a whole class of modal problems — focus trapping,
            body scroll locking, and the back button — none of which a filter
            needs to own.
          */}
          {filtersOpen && (
            <section
              id="shop-filters"
              aria-label="Filters"
              className="mb-8 border-y border-line py-2 lg:hidden"
            >
              <FilterPanel
                filters={filters}
                onToggleTerm={toggleTerm}
                onSetFilter={setFilter}
                onClear={clearFilters}
                hasFilters={hasFilters}
              />

              <div className="pb-4">
                <Button fullWidth size="sm" onClick={() => setFiltersOpen(false)}>
                  Show {meta?.total ?? 0} {meta?.total === 1 ? 'Result' : 'Results'}
                </Button>
              </div>
            </section>
          )}

          {/* Dimmed while a filter change is in flight — signals activity
              without discarding the current results. */}
          <div className={isPlaceholderData ? 'opacity-60 transition-opacity' : ''}>
            <ProductGrid
              products={products}
              isLoading={isPending}
              isError={isError}
              onRetry={() => void refetch()}
              isFiltered={hasFilters}
              onClearFilters={clearFilters}
            />
          </div>

          {meta && meta.lastPage > 1 && (
            <nav
              className="mt-14 flex items-center justify-center gap-4"
              aria-label="Pagination"
            >
              <Button
                variant="secondary"
                size="sm"
                disabled={meta.currentPage <= 1}
                onClick={() => setFilter({ page: meta.currentPage - 1 })}
              >
                Previous
              </Button>

              <span className="text-sm text-muted">
                Page {meta.currentPage} of {meta.lastPage}
              </span>

              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.hasMore}
                onClick={() => setFilter({ page: meta.currentPage + 1 })}
              >
                Next
              </Button>
            </nav>
          )}
        </div>
      </div>

    </div>
  );
}
